import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ShoppingCart, Plus, Minus, Trash2, Package, FileText, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';

export function CartDrawer() {
  const { items, removeItem, updateQuantity, clearCart, isOpen, setIsOpen, itemCount } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [placing, setPlacing] = useState(false);

  const handlePlaceOrder = async () => {
    if (!user || items.length === 0) return;
    setPlacing(true);
    try {
      // Create order
      const { data: order, error: orderErr } = await supabase
        .from('orders')
        .insert({ user_id: user.id, status: 'placed', total_items: itemCount })
        .select('id')
        .single();
      if (orderErr) throw orderErr;

      // Insert order items
      const orderItems = items.map(item => ({
        order_id: order.id,
        product_name: item.productName,
        product_id: item.productId,
        quantity: item.quantity,
        unit_price: item.price ?? null,
        image_url: item.imageUrl ?? null,
      }));

      const { error: itemsErr } = await supabase.from('order_items').insert(orderItems);
      if (itemsErr) throw itemsErr;

      clearCart();
      setIsOpen(false);
      toast({ title: '✅ Order Placed!', description: `${itemCount} item(s) submitted. You'll be notified when a wholesaler confirms.` });
      navigate('/spaza/orders');
    } catch (err: any) {
      toast({ title: 'Order Failed', description: err.message, variant: 'destructive' });
    } finally {
      setPlacing(false);
    }
  };

  const handleSendToRFQ = () => {
    setIsOpen(false);
    navigate('/spaza/rfq', {
      state: {
        prefillItems: items.map(i => ({
          product: i.productName,
          quantity: String(i.quantity),
          specs: '',
        })),
      },
    });
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="flex flex-col w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Your Cart
            {itemCount > 0 && (
              <Badge variant="secondary" className="ml-1">{itemCount}</Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3">
            <Package className="h-16 w-16 opacity-20" />
            <p className="font-medium">Your cart is empty</p>
            <p className="text-sm text-center">Browse the Product Index and add items you'd like to order.</p>
            <Button variant="outline" onClick={() => { setIsOpen(false); navigate('/spaza/products'); }}>
              Browse Products
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-3 py-3">
              {items.map(item => (
                <div key={item.productId} className="flex gap-3 p-3 rounded-lg border bg-card">
                  <div className="h-16 w-16 rounded-md bg-muted/30 flex-shrink-0 overflow-hidden flex items-center justify-center">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.productName} className="h-full w-full object-contain p-1" />
                    ) : (
                      <Package className="h-8 w-8 text-muted-foreground/20" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-foreground leading-tight line-clamp-2">{item.productName}</p>
                    {item.price && (
                      <p className="text-sm font-bold text-primary mt-0.5">R{item.price.toFixed(2)}</p>
                    )}
                    {item.store && (
                      <p className="text-xs text-muted-foreground">{item.store}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.productId, item.quantity - 1)}>
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="text-sm font-semibold w-8 text-center">{item.quantity}</span>
                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.productId, item.quantity + 1)}>
                        <Plus className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive ml-auto" onClick={() => removeItem(item.productId)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Separator />

            <div className="py-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total items</span>
                <span className="font-bold">{itemCount}</span>
              </div>
              {items.some(i => i.price) && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Est. total</span>
                  <span className="font-bold text-primary">
                    R{items.reduce((sum, i) => sum + (i.price ?? 0) * i.quantity, 0).toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            <SheetFooter className="flex-col gap-2 sm:flex-col">
              <Button className="w-full" size="lg" onClick={handlePlaceOrder} disabled={placing}>
                {placing ? 'Placing...' : 'Place Order'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" className="w-full" onClick={handleSendToRFQ}>
                <FileText className="mr-2 h-4 w-4" />
                Send as RFQ Instead
              </Button>
              <Button variant="ghost" className="w-full text-muted-foreground" onClick={clearCart}>
                Clear Cart
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
